// Integrytas Group - Common Security Module (CSM)
// Wraps a data grid and applies column personalization: visibility, order, width.
// In personalization mode, shows a column manager panel with drag-to-reorder.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react';
import { usePersonalization } from '../../lib/use_personalization';
import { registerFlnField } from '../../lib/fln_fields';
import type { ColumnPreference } from '../../types/personalization';

export interface GridColumnDef {
  key:           string;
  header:        string;
  defaultWidth?: number;
}

// Standard className for a <table> rendered inside PersonalizedGrid's children render-prop.
// `min-w-full` lets the table fill a wide container instead of leaving dead space to its
// right; `w-max` still lets it grow past 100% (triggering horizontal scroll) when content
// genuinely needs more room. Because table-layout is `auto`, any surplus width the fill
// produces gets distributed across whichever columns have no hard cap — which is exactly
// what we want: the data columns (given only a soft `width`, no `max-width`) absorb it and
// truncate less. The one column that must NOT grow is the trailing action column — pin it
// with `style={{ width: '1%' }}` on its <th> (the classic "shrink to content, let siblings
// fill the rest" trick) or it will re-absorb 100% of the surplus itself, right back to the
// original bug.
export const GRID_TABLE_CLASS = 'min-w-full w-max border-collapse text-csm-md';

interface PersonalizedGridProps {
  componentKey: string;
  columns:      GridColumnDef[];
  children:     (visibleCols: GridColumnDef[], widths: Record<string, number | undefined>) => React.ReactNode;
  /** Optional backing table — declared by the page to aid field-lineage resolution. */
  sourceTable?: string;
  /** P-12: optional phone-width rendering. When provided, the table (children)
      renders only from the sm breakpoint up and this card list renders below it —
      wide relational tables are unusable at 375px (3 of 10+ columns visible,
      blind horizontal scrolling). Receives the same personalized column set so
      hidden columns drop from cards and column order sets card-line priority.
      Opt-in: pages without it keep the table at every width, as before. */
  renderMobileCards?: (visibleCols: GridColumnDef[]) => React.ReactNode;
}

export function PersonalizedGrid({ componentKey, columns, children, sourceTable, renderMobileCards }: PersonalizedGridProps) {
  const { t } = useTranslation();
  const {
    isPersonalizing, colPrefs, setColPrefs, resetComponent, resetTick,
    isInspecting, inspectField, inspectSelection, pageCode,
  } = usePersonalization();
  const defaultKeys = columns.map(c => c.key);
  const prefs = colPrefs(componentKey, defaultKeys);

  // Derive the visible, ordered columns
  const orderedVisible = columns
    .slice()
    .sort((a, b) => (prefs[a.key]?.order ?? 0) - (prefs[b.key]?.order ?? 0))
    .filter(c => prefs[c.key]?.visible !== false);

  const widths: Record<string, number | undefined> = {};
  for (const c of columns) widths[c.key] = prefs[c.key]?.width ?? c.defaultWidth;

  // P-12: dual render — card list below sm, table from sm up (opt-in).
  const renderBody = () =>
    renderMobileCards ? (
      <>
        <div className="sm:hidden">{renderMobileCards(orderedVisible)}</div>
        <div className="hidden sm:block">{children(orderedVisible, widths)}</div>
      </>
    ) : (
      children(orderedVisible, widths)
    );

  // Self-register each column for field lineage (FLN) — coverage by registration.
  const colSig = columns.map(c => `${c.key}:${c.header}`).join('|');
  useEffect(() => {
    for (const c of columns) registerFlnField({ page_code: pageCode, component_key: componentKey, field_key: c.key, label: c.header, source_table: sourceTable });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCode, componentKey, colSig, sourceTable]);

  // Inspect mode: the table stays visible (informational), with a bar of column
  // chips above it — clicking one reveals that column's data source. Grid headers
  // are page-rendered, so the chip bar is the uniform, page-agnostic click target.
  if (isInspecting) {
    return (
      <>
        <div className="mb-2 rounded-csm-card border border-primary/30 bg-primary/5 p-2">
          <div className="mb-1 text-csm-2xs font-semibold uppercase tracking-wider text-primary">
            {t('fln.grid.inspect_columns', 'Columns — click to see where a column’s data comes from')}
          </div>
          <div className="flex flex-wrap gap-1">
            {orderedVisible.map(col => {
              const selected = inspectSelection?.component_key === componentKey && inspectSelection?.field_key === col.key;
              return (
                <button key={col.key} type="button" onClick={() => inspectField(componentKey, col.key, col.header)}
                  className={'rounded-csm-control border px-1.5 py-1 text-csm-xs ' +
                    (selected ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-ink hover:border-primary hover:text-primary')}>
                  {col.header}
                </button>
              );
            })}
          </div>
        </div>
        {renderBody()}
      </>
    );
  }

  if (!isPersonalizing) {
    return <>{renderBody()}</>;
  }

  return (
    <>
      <ColumnManager
        key={resetTick}
        componentKey={componentKey}
        columns={columns}
        prefs={prefs}
        setColPrefs={setColPrefs}
        onReset={() => resetComponent(componentKey)}
      />
      {renderBody()}
    </>
  );
}

// ---------------------------------------------------------------------------
// Column manager panel — shown only in personalization mode
// ---------------------------------------------------------------------------
interface ColumnManagerProps {
  componentKey: string;
  columns:      GridColumnDef[];
  prefs:        Record<string, ColumnPreference>;
  setColPrefs:  (key: string, cols: Record<string, Partial<ColumnPreference>>) => void;
  onReset:      () => void;
}

function ColumnManager({ componentKey, columns, prefs, setColPrefs, onReset }: ColumnManagerProps) {
  const { t } = useTranslation();
  const [localOrder, setLocalOrder] = useState<string[]>(() =>
    columns.slice().sort((a, b) => (prefs[a.key]?.order ?? 0) - (prefs[b.key]?.order ?? 0)).map(c => c.key)
  );

  const dragKey = useRef<string | null>(null);
  const dragOverKey = useRef<string | null>(null);

  const handleDragStart = useCallback((key: string) => { dragKey.current = key; }, []);
  const handleDragOver  = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault();
    dragOverKey.current = key;
  }, []);

  const handleDrop = useCallback(() => {
    if (!dragKey.current || !dragOverKey.current || dragKey.current === dragOverKey.current) return;
    const next = [...localOrder];
    const from = next.indexOf(dragKey.current);
    const to   = next.indexOf(dragOverKey.current);
    next.splice(from, 1);
    next.splice(to, 0, dragKey.current);
    setLocalOrder(next);
    const patch: Record<string, Partial<ColumnPreference>> = {};
    next.forEach((key, idx) => { patch[key] = { order: idx }; });
    setColPrefs(componentKey, patch);
    dragKey.current = null; dragOverKey.current = null;
  }, [localOrder, componentKey, setColPrefs]);

  const toggleVisible = useCallback((key: string) => {
    setColPrefs(componentKey, { [key]: { visible: !(prefs[key]?.visible ?? true) } });
  }, [componentKey, prefs, setColPrefs]);

  const setWidth = useCallback((key: string, w: string) => {
    const n = parseInt(w, 10);
    if (!isNaN(n) && n > 20) setColPrefs(componentKey, { [key]: { width: n } });
  }, [componentKey, setColPrefs]);

  const colMap = Object.fromEntries(columns.map(c => [c.key, c]));

  return (
    <div className="mb-2 rounded-csm-card border border-primary/30 bg-primary/5 p-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-primary">
          {t('common.grid.column_settings', 'Column Settings — drag to reorder')}
        </span>
        <button
          type="button"
          onClick={onReset}
          title={t('common.grid.reset_tooltip', "Remove this screen's column personalization at the current tier")}
          className="flex items-center gap-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs text-csm-muted hover:text-primary"
        >
          <RotateCcw size={10} /> {t('common.grid.reset', 'Reset')}
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        {localOrder.map(key => {
          const col  = colMap[key];
          const pref = prefs[key];
          const visible = pref?.visible ?? true;
          return (
            <div
              key={key}
              draggable
              onDragStart={() => handleDragStart(key)}
              onDragOver={e => handleDragOver(e, key)}
              onDrop={handleDrop}
              className={
                'flex items-center gap-1 rounded-csm-control border px-1.5 py-1 text-csm-xs cursor-grab active:cursor-grabbing ' +
                (visible ? 'border-csm-border bg-white text-csm-ink' : 'border-csm-border bg-csm-secondary text-csm-muted line-through')
              }
            >
              <GripVertical size={10} className="shrink-0 text-csm-muted" />
              <span>{col?.header ?? key}</span>
              <input
                type="number"
                min={40}
                max={600}
                defaultValue={pref?.width ?? col?.defaultWidth ?? ''}
                placeholder={t('common.grid.px', 'px')}
                onChange={e => setWidth(key, e.target.value)}
                className="w-10 rounded border border-csm-border px-1 text-csm-xs"
                title={t('common.grid.width_tooltip', 'Column width in pixels')}
              />
              <button
                type="button"
                onClick={() => toggleVisible(key)}
                title={visible ? t('common.grid.hide_column', 'Hide column') : t('common.grid.show_column', 'Show column')}
                className="shrink-0 text-csm-muted hover:text-primary"
              >
                {visible ? <Eye size={11} /> : <EyeOff size={11} />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
